import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import classnames from 'classnames';

import { connect } from 'react-redux';
import { compose } from 'redux';

import { selectTrackIsDownloading, selectTrackIsDownloaded } from '../../selectors/download';
import TitleWithPopularTitle from '../common/TitleWithPopularTitle';

import CapacitorRipple from '../capacitor/Ripple';
import InterpreterList from '../common/InterpreterList';
import ComposerList from '../common/ComposerList';
import DownloadIndicator from '../capacitor/DownloadIndicator';

import collectibleEntity, { collectibleEntityPropTypes } from '../../hoc/collectibleEntity';

import * as Shapes from '../../shapes/index';

import styles from './PlaylistItem.css';
import PlayButton from '../common/PlayButton';

import ContextMenu from '../util/ContextMenu';
import ContextMenuItem from '../util/ContextMenuItem';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import IconLabel from '../util/IconLabel';
import { ENTITY_TYPE_TRACK, ENTITY_TYPE_ALBUM } from '../../constants';
import AddToPlaylistContextMenuItem from '../common/AddToPlaylistContextMenuItem';
import { commonMessages } from '../messages/Common';
import FormattedTime from '../util/FormattedTime';
import ShareContextMenuItem from '../common/ShareContextMenuItem';
import shareableEntity, { shareableEntityPropTypes } from '../../hoc/shareableEntity';
import CollectionContextMenuItem from '../common/CollectionContextMenuItem';
import RemoveFromPlaylistContextMenuItem from '../common/RemoveFromPlaylistContextMenuItem';

class PlaylistItem extends PureComponent {
  static propTypes = {
    intl: intlShape.isRequired,
    track: Shapes.Track.isRequired,
    indexInPlaylist: PropTypes.number.isRequired,
    playlist: Shapes.Playlist,

    entityType: PropTypes.oneOf(['album', 'playlist', 'personal_playlist']),
    onClick: PropTypes.func,
    isCurrentTrack: PropTypes.bool,
    playing: PropTypes.bool,
    isDuplicatePiece: PropTypes.bool.isRequired,
    onAddToPlaylistButtonClick: PropTypes.func.isRequired,
    isInPersonalPlaylist: PropTypes.bool.isRequired,
    removeFromPlaylist: PropTypes.func,
    measure: PropTypes.func,

    dragHandleProps: PropTypes.object,
    trackIsDownloaded: PropTypes.bool,
    trackIsDownloading: PropTypes.bool,

    isBatchSelectMode: PropTypes.bool,
    isSelected: PropTypes.bool,
    onSelectTrack: PropTypes.func,

    ...collectibleEntityPropTypes,
    ...shareableEntityPropTypes,
  };

  static defaultProps = {
    isInPersonalPlaylist: false,
    isBatchSelectMode: false,
    isSelected: false,
  };

  onClick = playing => {
    const { onClick, track, indexInPlaylist } = this.props;
    onClick(track.id, indexInPlaylist, playing);
  };

  onAddToPlaylistClick = () => {
    this.props.onAddToPlaylistButtonClick(this.props.track.id);
  };

  onRemoveFromPlaylistClick = () => {
    const { indexInPlaylist, track } = this.props;

    this.props.removeFromPlaylist(track, indexInPlaylist);
  };

  renderRecordingInfo() {
    const track = this.props.track;
    const recording = track.recording;
    return (
      <div className={styles.recordingInfo}>
        <InterpreterList recording={recording} multiline={false} />
      </div>
    );
  }

  static renderPieceTitle(workTitle, workpartTitle, piece) {
    if (workTitle !== piece.title) {
      return (
        <span>
          {workpartTitle && <span>{`${workpartTitle} • `}</span>}
          <TitleWithPopularTitle {...piece} />
        </span>
      );
    }
    return null;
  }

  renderInfoDetail() {
    const track = this.props.track;
    const workpartTitle = track.piece.workpart.title;
    const { work } = track.recording;
    return (
      <div data-test="playlist-item.info-container">
        <div key={track.id} className={styles.detailComposer}>
          {__CAPACITOR__ && (
            <DownloadIndicator
              trackIsDownloaded={this.props.trackIsDownloaded}
              trackIsDownloading={this.props.trackIsDownloading}
              className={styles.downloadIndicator}
            />
          )}
          <ComposerList authors={work.authors} className={styles.link} />
        </div>
        <span className={styles.linkRecording}>
          <span className={styles.detailWorkTitle}>
            {work.title}
            <br />
          </span>
          {/*
            <br /> is used to achieve a line break between work-title and piece title but still benefit from the display: inline characteristic.
            This wasn't achievable with inline-block, nor any other display property bc we have a <Link> wrapping both elements.
          */}
          <span className={styles.detailPieceTitle}>
            {PlaylistItem.renderPieceTitle(work.title, workpartTitle, track.piece)}
          </span>
        </span>
        {this.renderRecordingInfo()}
        {this.props.track.duration && (
          <div className={styles.durationMobile}>
            <FormattedTime>{this.props.track.duration}</FormattedTime>
          </div>
        )}
      </div>
    );
  }

  renderReorderButton() {
    return (
      <button
        {...this.props.dragHandleProps}
        /* eslint-disable-next-line css-modules/no-undef-class */
        className={classnames(this.props.dragHandleProps.className, styles.dragHandle)}
        aria-label={this.props.intl.formatMessage(commonMessages.reorder)}
      >
        <IconLabel name="menu" />
      </button>
    );
  }

  renderCheckbox() {
    const { isSelected, indexInPlaylist, onSelectTrack, intl } = this.props;
    return (
      <button
        className={classnames(styles.checkbox, { [styles.checkboxSelected]: isSelected })}
        role="checkbox"
        aria-checked={isSelected}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onSelectTrack(indexInPlaylist);
        }}
        aria-label={
          isSelected
            ? intl.formatMessage(commonMessages.deselectTrack)
            : intl.formatMessage(commonMessages.selectTrack)
        }
        data-test="playlist-item.checkbox"
      >
        <span className={styles.checkboxInner}>
          {isSelected && <IconLabel name="check" size="small" />}
        </span>
      </button>
    );
  }

  renderContextMenu() {
    const {
      isInPersonalPlaylist,
      isInCollection,
      toggleIsInCollection,
      showShareModal,
      entityType,
    } = this.props;

    return (
      <ContextMenu
        data-test="playlist-item.context-menu"
        sideOffset={entityType === ENTITY_TYPE_ALBUM ? 50 : 150}
        side="top"
        align="start"
      >
        <CollectionContextMenuItem onClick={toggleIsInCollection} active={isInCollection} />
        <AddToPlaylistContextMenuItem onClick={this.onAddToPlaylistClick} />
        {isInPersonalPlaylist && (
          <RemoveFromPlaylistContextMenuItem onClick={this.onRemoveFromPlaylistClick} />
        )}
        <ContextMenuItem to={`/recordings/${this.props.track.recording.id}`}>
          <IconLabel
            name="recording"
            size="default"
            labelVisible
            label={
              <FormattedMessage
                id="playlist-item.context-menu.view-recording"
                defaultMessage="View Recording"
              />
            }
          />
        </ContextMenuItem>
        <ContextMenuItem to={`/works/${this.props.track.recording.work.id}`}>
          <IconLabel
            name="composition"
            size="default"
            labelVisible
            label={
              <FormattedMessage
                id="playlist-item.context-menu.view-work"
                defaultMessage="View Work"
              />
            }
          />
        </ContextMenuItem>
        {entityType !== ENTITY_TYPE_ALBUM &&
          this.props.track.recording.albums &&
          this.props.track.recording.albums.length > 0 && (
            <ContextMenuItem to={`/albums/${this.props.track.recording.albums[0]}`}>
              <IconLabel
                name="resource-audio"
                size="default"
                labelVisible
                label={
                  <FormattedMessage
                    id="playlist-item.context-menu.view-album"
                    defaultMessage="View Album"
                  />
                }
              />
            </ContextMenuItem>
          )}
        <ShareContextMenuItem
          onClick={showShareModal}
          text={this.props.intl.formatMessage(commonMessages.shareTrack)}
        />
      </ContextMenu>
    );
  }

  render() {
    const {
      isCurrentTrack,
      playing,
      isInPersonalPlaylist,
      isBatchSelectMode,
      isSelected,
      onSelectTrack,
      indexInPlaylist,
    } = this.props;

    const playingThisTrack = isCurrentTrack && playing;

    return (
      <div
        className={classnames(styles.item, {
          [styles.playingIndicator]: playingThisTrack,
          [styles.batchSelectMode]: isBatchSelectMode,
          [styles.selected]: isSelected,
        })}
        data-test="playlist-item"
        onClick={
          isBatchSelectMode
            ? e => {
                e.preventDefault();
                onSelectTrack(indexInPlaylist);
              }
            : undefined
        }
        onKeyDown={
          isBatchSelectMode
            ? e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSelectTrack(indexInPlaylist);
                }
              }
            : undefined
        }
        role={isBatchSelectMode ? 'button' : undefined}
        tabIndex={isBatchSelectMode ? 0 : undefined}
      >
        {isBatchSelectMode && this.renderCheckbox()}
        <div className={styles.wrapper}>
          {!isBatchSelectMode && (
            <button
              className={classnames(styles.statusButton, 'c-link--expand-hitbox')}
              onClick={this.onClick}
              data-test="playlist-item.play-btn"
              aria-label={
                playingThisTrack
                  ? this.props.intl.formatMessage(commonMessages.pause)
                  : this.props.intl.formatMessage(commonMessages.play)
              }
            >
              <CapacitorRipple />
            </button>
          )}
          {!isBatchSelectMode && (
            <PlayButton
              className={classnames(styles.playButton, 'c-link--expand-hitbox')}
              iconClassName={styles.status}
              size="default"
              playing={playingThisTrack}
              onClick={this.onClick}
              data-test="playlist-item.play-btn"
            />
          )}
          <div className={styles.infoDetail}>{this.renderInfoDetail()}</div>
        </div>
        {this.props.track.duration && (
          <div className={styles.durationDesktop}>
            <FormattedTime>{this.props.track.duration}</FormattedTime>
          </div>
        )}
        {!isBatchSelectMode && (
          <div className={styles.actions}>
            {isInPersonalPlaylist && this.props.dragHandleProps && this.renderReorderButton()}
            {this.renderContextMenu()}
          </div>
        )}
      </div>
    );
  }
}

function getCollectibleEntityDescription(props) {
  return {
    id: props.track.id,
    trackingSource: props.entityType === ENTITY_TYPE_ALBUM ? 'Album' : 'Playlist',
    capacitorContextEntityType: props.entityType,
    capacitorContextEntityId: props.playlist ? props.playlist.id : undefined,
  };
}

function getShareTrackingContext(props) {
  return {
    sharedContentType: 'Track',
    sharedContentId: props.track.id,
    contextType: props.entityType === ENTITY_TYPE_ALBUM ? 'Album' : 'Playlist',
    contentId: props.playlist ? props.playlist.id : undefined,
  };
}

function getShareUrl(props) {
  return `/recordings/${props.track.recording.id}?trackId=${props.track.id}`;
}

export default compose(
  connect((state, props) => {
    if (!__CAPACITOR__ || !props.playlist || !props.playlist.id) {
      return {};
    }
    return {
      trackIsDownloading: selectTrackIsDownloading(state, props.playlist.id, props.track.id),
      trackIsDownloaded: selectTrackIsDownloaded(state, props.playlist.id, props.track.id),
    };
  }),
  collectibleEntity(ENTITY_TYPE_TRACK, getCollectibleEntityDescription),
  shareableEntity(ENTITY_TYPE_TRACK, getShareTrackingContext, { getShareUrl }),
  injectIntl
)(PlaylistItem);
